import type { CashflowTarget } from '@moment/horizon-data-access/proposal'
import type { RecurringCashflowRow } from '@/portfolio/proposals/cashflow-target/types'

import { IsoDateStr } from '@moment/domain-types'

// Mirrors portfolios' target_problems, which refuses the whole request when any target breaks these.
export const EARLIEST_CASHFLOW_DATE = IsoDateStr.parse('1900-01-01')
export const LATEST_CASHFLOW_DATE = IsoDateStr.parse('2200-12-31')
export const MAX_CASHFLOW_AMOUNT = 1e15

export interface BoundIssue {
  code:
    | 'amount_out_of_range'
    | 'override_out_of_range'
    | 'date_out_of_range'
    | 'start_not_before_end'
  index: number
}

// A negative amount is the API schema's refusal (and the planner's amount_not_positive), not this one's.
export const isBoundedAmount = (value: number) =>
  Number.isFinite(value) && value <= MAX_CASHFLOW_AMOUNT

export const isInRange = (date: IsoDateStr) =>
  date >= EARLIEST_CASHFLOW_DATE && date <= LATEST_CASHFLOW_DATE

// Stored dates are typed as ISO days but never checked at runtime; the backend refuses any other string.
const isReadableInRange = (date: string) => {
  const parsed = IsoDateStr.safeParse(date)
  return parsed.success && isInRange(parsed.data)
}

function problemsOf(target: CashflowTarget): BoundIssue['code'][] {
  const problems: BoundIssue['code'][] = []
  const { amount, start_date: start, end_date: end } = target
  if (amount != null && !isBoundedAmount(amount)) {
    problems.push('amount_out_of_range')
  }
  const overrides = Object.values(target.overrides ?? {})
  if (overrides.some((value) => !isBoundedAmount(value))) {
    problems.push('override_out_of_range')
  }
  if (!isReadableInRange(end) || (start != null && !isReadableInRange(start))) {
    problems.push('date_out_of_range')
  }
  if (start != null && start >= end) problems.push('start_not_before_end')
  return problems
}

/** Every target the backend would refuse at the request boundary, by index then rule. */
export function targetBoundIssues(targets: CashflowTarget[]): BoundIssue[] {
  return targets.flatMap((target, index) =>
    problemsOf(target).map((code) => ({ code, index })),
  )
}

/** False unless a row's dates parse and fall in the backend's range, so a preview of it stays bounded. */
export function rowDatesInRange(
  row: Pick<RecurringCashflowRow, 'startDate' | 'endDate'>,
): boolean {
  const end = IsoDateStr.safeParse(row.endDate)
  if (!end.success || !isInRange(end.data)) return false
  if (row.startDate === null) return true
  const start = IsoDateStr.safeParse(row.startDate)
  return start.success && isInRange(start.data)
}
